<template>
  <div
    class="dashboard-layout"
    :class="{
      'has-top-strips': hasTopStrips,
      'trial-lockout-active': subscriptionLockoutActive
    }"
    :style="hasTopStrips ? { '--strip-h': topStripHeight + 'px' } : null"
  >
    <TrialStrip
      v-if="verifyStripContent"
      :content="verifyStripContent"
      :offset-top="0"
      @cta="onVerifyStripCta"
    />
    <TrialStrip
      v-if="trialStripContent"
      :content="trialStripContent"
      :offset-top="hasVerifyStripVisible ? 40 : 0"
      @dismiss="trialStripDismissed = true"
      @cta="onTrialStripCta"
    />

    <TopNavbar
      :lastUpdated="lastUpdated"
      :userEmail="$root.userEmail || ''"
    >
      <template slot="filters">
        <slot name="navbarFilters"></slot>
      </template>
    </TopNavbar>

    <DashboardSidebar
      :items="sidebarItems"
      :currentPath="$route.path"
      :disabled="!onboardingComplete"
      @navigate="navigateTo"
    />

    <div class="main-content">
      <slot></slot>
    </div>

    <CanceledLockoutOverlay
      v-if="cancelLockoutActive"
      :strip-offset-px="topStripHeight"
    />
    <PastDueLockoutOverlay
      v-else-if="pastDueLockoutActive && !billingPaymentModalFlowActive"
      :strip-offset-px="topStripHeight"
    />
    <TrialLockoutOverlay
      v-else-if="trialLockoutActive"
      :strip-offset-px="topStripHeight"
    />
  </div>
</template>

<script>
import TopNavbar from './TopNavbar.vue'
import DashboardSidebar from './DashboardSidebar.vue'
import TrialStrip from './TrialStrip.vue'
import TrialLockoutOverlay from './TrialLockoutOverlay.vue'
import CanceledLockoutOverlay from './CanceledLockoutOverlay.vue'
import PastDueLockoutOverlay from './PastDueLockoutOverlay.vue'
import {
  trialStripContentFromSummary,
  shouldShowTrialStrip,
  shouldShowTrialLockout,
  shouldShowCanceledLockout,
  shouldShowPastDueLockout
} from '../../utils/subscriptionBilling'
import { requestOpenBillingPaymentModal } from '../../utils/billingUpgrade'
import {
  ACCOUNT_SETTINGS_PATH,
  verifyEmailStripContent
} from '../../utils/emailVerification'

var BILLING_PATH = '/settings/billing'
var STRIP_HEIGHT = 40
var VERIFY_STRIP_PATH_PREFIXES = ['/home', '/products', '/stock', '/fee-claims', '/settings']

function pathShowsVerifyStrip (path) {
  if (!path) return false
  for (var i = 0; i < VERIFY_STRIP_PATH_PREFIXES.length; i++) {
    var p = VERIFY_STRIP_PATH_PREFIXES[i]
    if (path === p || path.indexOf(p + '/') === 0) return true
  }
  return false
}

export default {
  name: 'DashboardLayout',
  components: {
    TopNavbar,
    DashboardSidebar,
    TrialStrip,
    TrialLockoutOverlay,
    CanceledLockoutOverlay,
    PastDueLockoutOverlay
  },
  props: {
    lastUpdated: {
      type: String,
      default: 'just now'
    }
  },
  data: function () {
    return {
      trialStripDismissed: false
    }
  },
  computed: {
    sidebarItems: function () {
      var items = [
        { id: 'home', label: 'Dashboard', icon: 'th icon', path: '/home' },
        { id: 'products', label: 'Products', icon: 'bookmark icon', path: '/products' },
        { id: 'issues', label: 'Alerts', icon: 'bell icon', path: '/issues' },
        { id: 'stock', label: 'Stock', icon: 'cube icon', path: '/stock' }
      ]
      // Tenants with feeClaims: Fee Claims, with the To claim count (hidden at 0).
      if (this.$root.feeClaims) {
        var count = this.$root.feeClaimsToClaimCount
        items.push({ id: 'feeClaims', label: 'Fee Claims', icon: 'dollar sign icon', path: '/fee-claims', badgeCount: count > 0 ? count : null })
      }
      items.push({ id: 'settings', label: 'Settings', icon: 'setting icon', path: '/settings' })
      return items
    },
    onboardingComplete: function () {
      var root = this.$parent && this.$parent.$parent && this.$parent.$parent.$parent
      return root && root.onboardingStatus ? root.onboardingStatus.completed : true
    },
    subscriptionSummary: function () {
      return this.$root.subscriptionSummary || null
    },
    billingContext: function () {
      return {
        hasDailyPricing: this.$root.hasDailyPricing,
        billingUserTypeRaw: this.$root.billingUserTypeRaw,
        billingSummary: this.$root.billingSummary,
        packageDetails: this.$root.billingPackageDetails,
        tenantId: this.$root.tenantId
      }
    },
    trialStripContent: function () {
      if (!shouldShowTrialStrip(this.subscriptionSummary, this.trialStripDismissed, this.billingContext)) {
        return null
      }
      return trialStripContentFromSummary(this.subscriptionSummary, this.billingContext)
    },
    hasTrialStripVisible: function () {
      return this.trialStripContent != null
    },
    hasVerifyStripVisible: function () {
      return this.$root.emailVerified === false && pathShowsVerifyStrip(this.$route.path)
    },
    verifyStripContent: function () {
      if (!this.hasVerifyStripVisible) return null
      return verifyEmailStripContent({
        email: this.$root.unverifiedEmail || this.$root.userEmail,
        verificationEmailSent: this.$root.verificationEmailSent,
        verificationEmailSending: this.$root.verificationEmailSending
      })
    },
    topStripHeight: function () {
      var n = (this.hasVerifyStripVisible ? 1 : 0) + (this.hasTrialStripVisible ? 1 : 0)
      return n * STRIP_HEIGHT
    },
    hasTopStrips: function () {
      return this.topStripHeight > 0
    },
    cancelLockoutActive: function () {
      return shouldShowCanceledLockout(this.subscriptionSummary, this.$root.paymentStatus)
    },
    billingPaymentModalFlowActive: function () {
      return !!(this.$root && this.$root.billingPaymentModalFlowActive)
    },
    pastDueLockoutActive: function () {
      if (this.billingPaymentModalFlowActive) return false
      return shouldShowPastDueLockout(this.subscriptionSummary, this.billingContext)
    },
    trialLockoutActive: function () {
      return shouldShowTrialLockout(this.subscriptionSummary, this.billingContext)
    },
    subscriptionLockoutActive: function () {
      if (this.billingPaymentModalFlowActive) return false
      return this.cancelLockoutActive || this.pastDueLockoutActive || this.trialLockoutActive
    }
  },
  watch: {
    '$route.path': function () {
      this.enforceSubscriptionLockoutRoute()
    },
    '$route.hash': function () {
      this.enforceSubscriptionLockoutRoute()
    },
    subscriptionLockoutActive: function (active) {
      if (active) this.enforceSubscriptionLockoutRoute()
    },
    '$root.paymentStatus': function () {
      if (this.subscriptionLockoutActive) {
        this.enforceSubscriptionLockoutRoute()
      }
    },
    subscriptionSummary: function () {
      if (this.subscriptionLockoutActive) {
        this.enforceSubscriptionLockoutRoute()
      }
    },
    '$root.authCompleted': function (done) {
      if (done) this.ensureEmailVerificationLoaded()
    },
    '$root.userEmail': function (email) {
      if (email) this.ensureEmailVerificationLoaded()
    },
    '$root.feeClaims': function (on) {
      if (on && this.$root.loadFeeClaimsCount) this.$root.loadFeeClaimsCount()
    }
  },
  mounted: function () {
    var self = this
    var schedule = this.$root.scheduleAfterFirstPaint
      ? this.$root.scheduleAfterFirstPaint.bind(this.$root)
      : function (fn) { setTimeout(fn, 50) }
    schedule(function () {
      self.ensureBillingContextOnRoot()
      if (self.$root.loadSubscriptionSummary) {
        self.$root.loadSubscriptionSummary()
      }
      self.ensureEmailVerificationLoaded()
      if (self.$root.feeClaims && self.$root.loadFeeClaimsCount) self.$root.loadFeeClaimsCount()
    })
    this._onVisibilityChange = function () {
      if (document.visibilityState === 'visible') {
        // Subscription is session-cached; only re-check email when banner may need update.
        self.ensureEmailVerificationLoaded(true)
      }
    }
    document.addEventListener('visibilitychange', this._onVisibilityChange)
    this.enforceSubscriptionLockoutRoute()
  },
  beforeDestroy: function () {
    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange)
    }
  },
  methods: {
    /**
     * Call identity APIs when verification is unset. Pass force=true to refresh
     * even if we already have a value (e.g. tab focus while banner is visible).
     */
    ensureEmailVerificationLoaded: function (force) {
      var root = this.$root
      if (!root || !root.loadEmailVerificationStatus) return
      if (!force && root.emailVerified != null) return
      if (!root.userEmail && !root.authCompleted) return
      root.loadEmailVerificationStatus(!!force)
    },
    ensureBillingContextOnRoot: function () {
      var root = this.$root
      if (!root) return
      if (root.billingUserTypeRaw != null) {
        this.ensureEmailVerificationLoaded()
        return
      }
      if (!root.loadIdentitySettings) {
        this.ensureEmailVerificationLoaded()
        return
      }
      root.loadIdentitySettings().then(function () {
        // Do not apply verification from this billing-only fetch — always use
        // loadEmailVerificationStatus so email-addresses can override stale settings.
        if (root.loadEmailVerificationStatus && root.emailVerified == null) {
          root.loadEmailVerificationStatus()
        }
      }, function () {
        if (root.loadEmailVerificationStatus && root.emailVerified == null) {
          root.loadEmailVerificationStatus()
        }
      })
    },
    isBillingPath: function (path) {
      if (!path) return false
      return path === BILLING_PATH || path.indexOf(BILLING_PATH + '/') === 0
    },
    billingLockoutTarget: function () {
      return BILLING_PATH
    },
    enforceSubscriptionLockoutRoute: function () {
      if (!this.subscriptionLockoutActive) return
      var target = this.billingLockoutTarget
      if (this.$route.fullPath === target) return
      if (!this.pastDueLockoutActive && this.isBillingPath(this.$route.path)) return
      this.$router.replace(target)
    },
    navigateTo: function (path) {
      if (this.subscriptionLockoutActive && !this.isBillingPath(path)) {
        this.$router.push(this.billingLockoutTarget)
        return
      }
      if (this.$route.path !== path) {
        this.$router.push(path)
      }
    },
    onVerifyStripCta: function () {
      var self = this
      if (!this.$root.resendEmailVerification) {
        if (this.$route.path !== ACCOUNT_SETTINGS_PATH) {
          this.$router.push(ACCOUNT_SETTINGS_PATH)
        }
        return
      }
      this.$root.resendEmailVerification().then(function () {}, function () {
        if (self.$route.path !== ACCOUNT_SETTINGS_PATH) {
          self.$router.push(ACCOUNT_SETTINGS_PATH)
        }
      })
    },
    onTrialStripCta: function (hash) {
      var content = this.trialStripContent
      if (content && content.ctaAction === 'resume' && this.$root.resumeSubscription) {
        var self = this
        this.$root.resumeSubscription().then(function () {
          if (self.$route.path !== BILLING_PATH) {
            self.$router.push(BILLING_PATH)
          }
        }, function () {
          if (self.$route.path !== BILLING_PATH) {
            self.$router.push(BILLING_PATH)
          }
        })
        return
      }
      if (hash === '#payment-method') {
        requestOpenBillingPaymentModal(this)
        return
      }
      var target = BILLING_PATH + (hash || '')
      if (this.$route.fullPath !== target) {
        this.$router.push(target)
      }
    }
  }
}
</script>

<style scoped>
.dashboard-layout {
  min-height: 100vh;
  background: var(--bg-white);
}

.main-content {
  padding-left: 188px;
  padding-top: 60px;
  background: var(--bg-white);
}
</style>

<style>
.dashboard-layout.has-top-strips .top-navbar {
  top: var(--strip-h, 40px);
}

.dashboard-layout.has-top-strips .dashboard-sidebar {
  top: var(--strip-h, 40px);
}

.dashboard-layout.has-top-strips .main-content {
  padding-top: calc(60px + var(--strip-h, 40px));
}

.dashboard-layout.has-top-strips .issues-navbar-filters {
  top: var(--strip-h, 40px);
}
</style>



// WEBPACK FOOTER //
// DashboardLayout.vue?d6ec4272